import { useQueryClient } from '@tanstack/react-query';
import {
  useApiMutation,
  useApiQuery,
  useApiQueryWithTransport,
  type Transport,
} from '@/hooks/use-api-query';
import { useFetch } from '@/hooks/use-fetch';
import { qk } from '@/lib/query/keys';

/** GET /meta/channels/:id — per-channel detail (async WA fields). */
export interface ChannelDetailResponse {
  whatsappPhoneNumberId?: string | null;
  whatsappBusinessName?: string | null;
}

/** POST /meta/channels/:id/refresh — re-pull from Meta; may report deletion. */
export interface ChannelRefreshResponse {
  deleted?: boolean;
  whatsappWabaName?: string | null;
  whatsappBusinessName?: string | null;
  whatsappQualityRating?: string | null;
  consecutiveForwardFailures?: number;
}

/** GET /meta/channels/:id/token — the HookMyApp gateway bearer (hmat_…). */
export interface ChannelTokenInfo {
  token: string;
  tokenPrefix: string;
  tokenSuffix: string;
}

export function channelDetailKey(wsId: string, channelId: string) {
  return qk.workspace(wsId, 'channel', channelId, 'detail');
}

export function channelTokenKey(wsId: string, channelId: string) {
  return qk.workspace(wsId, 'channel', channelId, 'token');
}

export function useChannelDetail(
  wsId: string | undefined,
  channelId: string,
  sessionScoped: boolean,
) {
  return useApiQuery<ChannelDetailResponse>(
    channelDetailKey(wsId ?? '', channelId),
    `/meta/channels/${channelId}`,
    { enabled: !!wsId && sessionScoped },
  );
}

// The token widget fails soft (inline "Couldn't load" + Retry), so its GET
// opts out of the global full-screen ErrorPage escalation — a token 5xx must
// not nuke the whole app.
export function useChannelToken(
  wsId: string | undefined,
  channelId: string,
  sessionScoped: boolean,
) {
  const fetchWithAuth = useFetch();
  const transport: Transport = (path, init) =>
    fetchWithAuth(path, init, { suppressGlobalError: true });
  return useApiQueryWithTransport<ChannelTokenInfo>(
    channelTokenKey(wsId ?? '', channelId),
    `/meta/channels/${channelId}/token`,
    transport,
    { enabled: !!wsId && sessionScoped },
  );
}

export function useRotateChannelToken(wsId: string | undefined) {
  const qc = useQueryClient();
  return useApiMutation<ChannelTokenInfo, { channelId: string }>(
    ({ channelId }) => ({
      path: `/meta/channels/${channelId}/token/rotate`,
      init: { method: 'POST' },
      fallback: 'Could not rotate the access token. Try again.',
    }),
    {
      onSuccess: (data, { channelId }) => {
        // Seed the rotated token straight into the cache — no refetch needed,
        // and the widget's missing/unavailable states clear with it.
        if (wsId) qc.setQueryData(channelTokenKey(wsId, channelId), data);
      },
    },
  );
}

// The refresh POST re-pulls names/quality/subscription from Meta server-side,
// so the channel-detail read is stale afterwards — invalidate it.
export function useRefreshChannel(wsId: string | undefined) {
  const qc = useQueryClient();
  return useApiMutation<ChannelRefreshResponse, { channelId: string }>(
    ({ channelId }) => ({
      path: `/meta/channels/${channelId}/refresh`,
      init: { method: 'POST' },
    }),
    {
      onSuccess: (_data, { channelId }) => {
        if (wsId) {
          void qc.invalidateQueries({ queryKey: channelDetailKey(wsId, channelId) });
        }
      },
    },
  );
}

// Per-channel messaging toggle. The channels list itself is context-owned
// (workspace-context refreshChannels — Batch E), so call sites keep calling
// refreshChannels()/onChannelUpdated() after these succeed.
export function useSetChannelForwarding() {
  return useApiMutation<unknown, { channelId: string; enabled: boolean }>(
    ({ channelId, enabled }) => ({
      path: `/meta/channels/${channelId}/${enabled ? 'enable' : 'disable'}`,
      init: { method: 'POST' },
    }),
  );
}

export function useSetAllForwarding() {
  return useApiMutation<unknown, { enabled: boolean }>(({ enabled }) => ({
    path: '/meta/channels/forwarding',
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    },
    fallback: `Failed to ${enabled ? 'enable' : 'disable'} channels`,
  }));
}

export function useSetMetaRetry() {
  return useApiMutation<unknown, { channelId: string; enabled: boolean }>(
    ({ channelId, enabled }) => ({
      path: `/meta/channels/${channelId}/meta-retry`,
      init: { method: 'POST', body: JSON.stringify({ enabled }) },
    }),
  );
}

/** DELETE /webhook-config/:id — switch the channel destination back to the CLI. */
export function useClearWebhookConfig() {
  return useApiMutation<unknown, { channelId: string }>(({ channelId }) => ({
    path: `/webhook-config/${channelId}`,
    init: { method: 'DELETE' },
  }));
}

// Rotates the per-channel HMAC signing secret — SEPARATE from the verify
// token, which is never touched by any hook in this file.
export function useRotateHmac() {
  return useApiMutation<{ hmacSecret: string }, { channelId: string }>(
    ({ channelId }) => ({
      path: `/webhook-config/${channelId}/rotate-hmac`,
      init: { method: 'POST' },
    }),
  );
}

export function useDisconnectChannel() {
  return useApiMutation<unknown, { channelId: string }>(({ channelId }) => ({
    path: `/meta/channels/${channelId}/disconnect`,
    init: { method: 'POST' },
  }));
}

// AIT-713: the profile photo is an upload for a handle, then a profile update
// carrying that handle. The stored avatar refreshes via the channel refresh.
export function useUploadWhatsAppHandle() {
  return useApiMutation<{ handle: string }, { channelId: string; file: File }>(({ channelId, file }) => {
    const form = new FormData();
    form.append('file', file);
    return { path: `/channels/${channelId}/whatsapp/uploads`, init: { method: 'POST', body: form } };
  });
}

export function useUpdateWhatsAppProfile() {
  return useApiMutation<{ updated: string[] }, { channelId: string; profilePictureHandle: string }>(
    ({ channelId, profilePictureHandle }) => ({
      path: `/channels/${channelId}/whatsapp/profile`,
      init: { method: 'PATCH', body: JSON.stringify({ profilePictureHandle }) },
    }),
  );
}
